import { IFileState, systemRights } from "@tedivo/tvd-api-models";

import { IOpenVesselDefinitionV1 } from "open-vessel-definition";
import { sanitizeString } from "@tedivo/tedivo-pure-helpers";
import securityModule from "../../../../../../../app/security/SecurityModule";
import stafSaveOptions from "../../../../../../../app/stores/stafSaveOptions";
import { startDownloadInBrowser } from "@tedivo/tedivo-dom-helpers";

/**
 * Whether the current user may download a file of their own organization with the given
 * right. A Tedivo admin always can; everybody else needs a non-expired plan, the right,
 * a published (non-DRAFT) file and a file that belongs to their organization.
 *
 * Shared by the editor's Download menu and the file list of My Cloud, so both offer the
 * download under the same conditions.
 */
export function canDownloadOwnOrgFile({
  right,
  fileOrgId,
  fileState,
  extra,
}: {
  right: string;
  fileOrgId: string;
  fileState: IFileState;
  extra?: () => boolean;
}): boolean {
  if (securityModule.planIsReadOnly) return false;
  if (securityModule.userIsTedivoAdmin) return true;

  return (
    fileState !== "DRAFT" &&
    securityModule.userHasPermission(right) &&
    fileOrgId === securityModule.currentOrganizationId &&
    (!extra || extra())
  );
}

/** The gate for the OVD JSON download. */
export function canDownloadOvdJson(fileOrgId: string, fileState: IFileState): boolean {
  return canDownloadOwnOrgFile({
    right: systemRights.ORG.DownloadOwnOrgFileAsOvd,
    fileOrgId,
    fileState,
  });
}

/** Downloads the OVD as `<sanitized name>.json`, honouring the "pretty JSON" preference. */
export function downloadOvdJsonInBrowser(json: IOpenVesselDefinitionV1, name: string) {
  downloadBlobInBrowser(
    new Blob([JSON.stringify(json, null, stafSaveOptions.isFormattedJsonSaved ? 2 : 0)], {
      type: "application/json",
    }),
    `${sanitizeString(name || "")}.json`,
  );
}

/** Starts a download and releases the object URL afterwards, so the Blob is not
 *  held in memory for the whole life of the document. */
export function downloadBlobInBrowser(blob: Blob | undefined, fileName: string) {
  if (!blob) return;

  const url = URL.createObjectURL(blob);

  startDownloadInBrowser(url, fileName);

  // Revoking synchronously can cancel the download in some browsers, so it waits
  // until the click that started it has been processed.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
